/**
 * Voting Service
 * 
 * Handles voting on trip items (itinerary items, suggestions, places, activities)
 * for collaborative decision making
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  Timestamp,
  deleteField,
  runTransaction,
  documentId,
  type Unsubscribe,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/services/logger';

export type VoteType = 'upvote' | 'downvote' | 'neutral';

export type ItemType = 'itinerary_item' | 'suggestion' | 'place' | 'activity';

export type TripVote = {
  id: string;
  tripId: string;
  itemId: string; // Itinerary item ID or suggestion ID
  itemType: ItemType;
  voterId: string;
  vote: VoteType;
  comment?: string;
  createdAt: Date;
};

export type FirestoreTripVote = {
  tripId: string;
  itemId: string;
  itemType: ItemType;
  voterId: string;
  vote: VoteType;
  comment?: string;
  createdAt: Timestamp | Date;
};

export type VoteAggregate = {
  itemId: string;
  itemType: ItemType;
  upvotes: number;
  downvotes: number;
  totalVotes: number;
  score: number; // upvotes - downvotes
  voters: string[]; // User IDs who voted
  lastUpdated: Date;
};

type FirestoreVoteAggregate = {
  tripId: string;
  itemId: string;
  itemType: ItemType;
  upvotes: number;
  downvotes: number;
  totalVotes: number;
  score: number;
  voters: string[];
  voterVotes?: Record<string, VoteType>;
  lastUpdated?: Timestamp | Date;
};

const isOfflineError = (err: unknown): boolean => {
  const code = (err as { code?: string })?.code;
  return code === 'unavailable' || code === 'failed-precondition';
};

const syncTripMembershipIfNeeded = async (tripId: string, error: unknown): Promise<boolean> => {
  const { isPermissionError, syncTripMembership } = await import('@/services/tripMembership');
  if (!isPermissionError(error)) {
    logger.debug('[Voting] Not a permission error, skipping membership sync', { tripId });
    return false;
  }
  logger.info('[Voting] Permission error detected, attempting membership repair', { tripId });
  try {
    const res = await syncTripMembership(tripId);
    if (res?.success) {
      logger.info('[Voting] Membership repair succeeded', { tripId });
      // Small delay to allow Firestore rules to propagate
      await new Promise(resolve => setTimeout(resolve, 300));
      return true;
    }
    logger.warn('[Voting] Membership repair returned success=false', { tripId });
    return false;
  } catch (syncError) {
    logger.error('[Voting] Membership repair failed', syncError instanceof Error ? syncError : new Error(String(syncError)));
    return false;
  }
};

/**
 * Generate vote document ID from trip, item, and voter
 */
const getVoteDocId = (tripId: string, itemId: string, voterId: string): string => {
  return `${tripId}_${itemId}_${voterId}`;
};

/**
 * Convert Firestore vote to app vote
 */
const convertVote = (id: string, data: FirestoreTripVote): TripVote => {
  return {
    id,
    tripId: data.tripId,
    itemId: data.itemId,
    itemType: data.itemType,
    voterId: data.voterId,
    vote: data.vote,
    comment: data.comment,
    createdAt: data.createdAt instanceof Timestamp 
      ? data.createdAt.toDate() 
      : data.createdAt instanceof Date 
        ? data.createdAt 
        : new Date(),
  };
};

const getAggregateDocId = (tripId: string, itemId: string, itemType: ItemType): string =>
  `${tripId}_${itemType}_${itemId}`;

const aggregateCollection = () => collection(db, 'tripVoteAggregates');

const emptyAggregate = (itemId: string, itemType: ItemType): VoteAggregate => ({
  itemId,
  itemType,
  upvotes: 0,
  downvotes: 0,
  totalVotes: 0,
  score: 0,
  voters: [],
  lastUpdated: new Date(),
});

const toVoteAggregate = (
  itemId: string,
  itemType: ItemType,
  raw: FirestoreVoteAggregate | undefined,
): VoteAggregate => {
  if (!raw) {
    return emptyAggregate(itemId, itemType);
  }
  const lastUpdatedRaw = raw.lastUpdated;
  const lastUpdated =
    lastUpdatedRaw instanceof Timestamp
      ? lastUpdatedRaw.toDate()
      : lastUpdatedRaw instanceof Date
        ? lastUpdatedRaw
        : new Date();
  return {
    itemId,
    itemType,
    upvotes: Math.max(0, Number(raw.upvotes || 0)),
    downvotes: Math.max(0, Number(raw.downvotes || 0)),
    totalVotes: Math.max(0, Number(raw.totalVotes || 0)),
    score: Number(raw.score || 0),
    voters: Array.isArray(raw.voters) ? raw.voters.filter((v): v is string => typeof v === 'string') : [],
    lastUpdated,
  };
};

/**
 * Check if user is a collaborator on the trip
 * If not found initially, proactively attempts membership sync before failing
 */
const checkCollaboratorAccess = async (
  tripId: string,
  userId: string
): Promise<{ canVote: boolean; error?: string }> => {
  try {
    if (__DEV__) {
      console.log('[Voting] checkCollaboratorAccess');
    }
    
    const tripRef = doc(db, 'trips', tripId);
    let tripDoc: Awaited<ReturnType<typeof getDoc>>;
    try {
      tripDoc = await getDoc(tripRef);
    } catch (error) {
      if (__DEV__) {
        console.log('[Voting] Error fetching trip, attempting membership sync', { error });
      }
      const didSync = await syncTripMembershipIfNeeded(tripId, error);
      if (didSync) {
        tripDoc = await getDoc(tripRef);
      } else {
        throw error;
      }
    }
    
    if (!tripDoc.exists()) {
      logger.warn('[Voting] Trip not found', { tripId });
      return { canVote: false, error: 'Trip not found' };
    }
    
    const tripData = tripDoc.data() as DocumentData;
    
    // Owner can always vote
    if (tripData.owner === userId) {
      if (__DEV__) {
        console.log('[Voting] User is owner, can vote');
      }
      return { canVote: true };
    }
    
    // Check if user is a collaborator
    const collaborators = Array.isArray(tripData.collaborators) ? tripData.collaborators : [];
    const members = Array.isArray(tripData.members) ? tripData.members : [];
    const memberIds = Array.isArray(tripData.memberIds) ? tripData.memberIds : [];
    
    const isCollaborator = collaborators.some(
      (c: any) => c.userId === userId
    ) || members.includes(userId) || memberIds.includes(userId);
    
    if (__DEV__) {
      console.log('[Voting] Collaborator check', { 
        userId, 
        isCollaborator, 
        collaboratorCount: collaborators.length, 
        memberCount: members.length,
        memberIdsCount: memberIds.length,
      });
    }
    
    if (!isCollaborator) {
      // Proactively try membership sync before failing
      // This handles cases where the user's membership wasn't synced yet
      logger.info('[Voting] User not found as collaborator, attempting proactive membership sync', { tripId, userId });
      
      try {
        const { syncTripMembership } = await import('@/services/tripMembership');
        const syncResult = await syncTripMembership(tripId);
        
        if (syncResult?.success) {
          // Re-fetch trip data after sync
          await new Promise(resolve => setTimeout(resolve, 300));
          const refreshedDoc = await getDoc(tripRef);
          
          if (refreshedDoc.exists()) {
            const refreshedData = refreshedDoc.data() as DocumentData;
            const refreshedCollaborators = Array.isArray(refreshedData.collaborators) ? refreshedData.collaborators : [];
            const refreshedMembers = Array.isArray(refreshedData.members) ? refreshedData.members : [];
            const refreshedMemberIds = Array.isArray(refreshedData.memberIds) ? refreshedData.memberIds : [];
            
            const isNowCollaborator = refreshedCollaborators.some(
              (c: any) => c.userId === userId
            ) || refreshedMembers.includes(userId) || refreshedMemberIds.includes(userId);
            
            if (isNowCollaborator) {
              if (__DEV__) {
                console.log('[Voting] User is now collaborator after sync');
              }
              return { canVote: true };
            }
          }
        }
      } catch (syncError) {
        logger.warn('[Voting] Proactive membership sync failed', syncError instanceof Error ? syncError : new Error(String(syncError)));
      }
      
      return { canVote: false, error: 'Only trip collaborators can vote' };
    }
    
    return { canVote: true };
  } catch (error) {
    logger.error('[Voting] Error checking collaborator access', error instanceof Error ? error : new Error(String(error)));
    if (isOfflineError(error)) {
      return { canVote: false, error: "You're offline. Check your connection and try again." };
    }
    const { isPermissionError } = await import('@/services/tripMembership').catch(() => ({ isPermissionError: () => false }));
    if (isPermissionError(error)) {
      return { canVote: false, error: "You don't have access to vote on this trip." };
    }
    return { canVote: false, error: 'Failed to verify access' };
  }
};

/**
 * Cast a vote on an item
 */
export const castVote = async (
  tripId: string,
  itemId: string,
  itemType: ItemType,
  vote: VoteType,
  userId: string,
  comment?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Check if user can vote
    const accessCheck = await checkCollaboratorAccess(tripId, userId);
    if (!accessCheck.canVote) {
      return { success: false, error: accessCheck.error || 'Cannot vote' };
    }
    
    const voteId = getVoteDocId(tripId, itemId, userId);
    const voteRef = doc(db, 'tripVotes', voteId);
    const aggregateRef = doc(aggregateCollection(), getAggregateDocId(tripId, itemId, itemType));
    
    const voteData: FirestoreTripVote = {
      tripId,
      itemId,
      itemType,
      voterId: userId,
      vote,
      ...(comment != null && comment !== '' ? { comment } : {}),
      createdAt: Timestamp.now(),
    };
    
    try {
      await runTransaction(db, async (tx) => {
        const [existingVoteDoc, aggregateDoc] = await Promise.all([
          tx.get(voteRef),
          tx.get(aggregateRef),
        ]);

        if (existingVoteDoc.exists()) {
          tx.update(voteRef, {
            vote,
            comment: comment != null && comment !== '' ? comment : deleteField(),
            createdAt: Timestamp.now(),
          });
        } else {
          tx.set(voteRef, voteData);
        }

        const aggregateData = aggregateDoc.exists()
          ? (aggregateDoc.data() as FirestoreVoteAggregate)
          : undefined;
        const voterVotes: Record<string, VoteType> = {
          ...(aggregateData?.voterVotes || {}),
        };

        if (vote === 'neutral') {
          delete voterVotes[userId];
        } else {
          voterVotes[userId] = vote;
        }

        let upvotes = 0;
        let downvotes = 0;
        for (const voteValue of Object.values(voterVotes)) {
          if (voteValue === 'upvote') upvotes += 1;
          if (voteValue === 'downvote') downvotes += 1;
        }
        const totalVotes = upvotes + downvotes;
        const score = upvotes - downvotes;

        tx.set(
          aggregateRef,
          {
            tripId,
            itemId,
            itemType,
            upvotes,
            downvotes,
            totalVotes,
            score,
            voters: Object.keys(voterVotes),
            voterVotes,
            lastUpdated: Timestamp.now(),
          },
          { merge: true },
        );
      });
    } catch (error) {
      if (isOfflineError(error)) {
        return { success: false, error: "You're offline. Check your connection and try again." };
      }
      const didSync = await syncTripMembershipIfNeeded(tripId, error);
      if (didSync) {
        await runTransaction(db, async (tx) => {
          const [existingVoteDoc, aggregateDoc] = await Promise.all([
            tx.get(voteRef),
            tx.get(aggregateRef),
          ]);

          if (existingVoteDoc.exists()) {
            tx.update(voteRef, {
              vote,
              comment: comment != null && comment !== '' ? comment : deleteField(),
              createdAt: Timestamp.now(),
            });
          } else {
            tx.set(voteRef, voteData);
          }

          const aggregateData = aggregateDoc.exists()
            ? (aggregateDoc.data() as FirestoreVoteAggregate)
            : undefined;
          const voterVotes: Record<string, VoteType> = {
            ...(aggregateData?.voterVotes || {}),
          };

          if (vote === 'neutral') {
            delete voterVotes[userId];
          } else {
            voterVotes[userId] = vote;
          }

          let upvotes = 0;
          let downvotes = 0;
          for (const voteValue of Object.values(voterVotes)) {
            if (voteValue === 'upvote') upvotes += 1;
            if (voteValue === 'downvote') downvotes += 1;
          }
          const totalVotes = upvotes + downvotes;
          const score = upvotes - downvotes;

          tx.set(
            aggregateRef,
            {
              tripId,
              itemId,
              itemType,
              upvotes,
              downvotes,
              totalVotes,
              score,
              voters: Object.keys(voterVotes),
              voterVotes,
              lastUpdated: Timestamp.now(),
            },
            { merge: true },
          );
        });
      } else {
        throw error;
      }
    }

    logger.info('[Voting] Vote cast successfully', { tripId, itemId, userId, vote });
    return { success: true };
  } catch (error) {
    logger.error('[Voting] Error casting vote', error instanceof Error ? error : new Error(String(error)));
    if (isOfflineError(error)) {
      return { success: false, error: "You're offline. Check your connection and try again." };
    }
    const { isPermissionError } = await import('@/services/tripMembership').catch(() => ({ isPermissionError: () => false }));
    if (isPermissionError(error)) {
      logger.warn('[Voting] Permission denied casting vote – check Firestore rules for tripVotes', {
        tripId,
        itemId,
        itemType,
        userId,
        code: (error as { code?: string })?.code,
      });
      return { success: false, error: "You don't have access to vote on this trip." };
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to cast vote',
    };
  }
};

/**
 * Remove a vote (set to neutral)
 */
export const removeVote = async (
  tripId: string,
  itemId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  return castVote(tripId, itemId, 'itinerary_item', 'neutral', userId);
};

/**
 * Get user's vote for an item
 */
export const getUserVote = async (
  tripId: string,
  itemId: string,
  userId: string
): Promise<TripVote | null> => {
  try {
    const voteId = getVoteDocId(tripId, itemId, userId);
    const voteRef = doc(db, 'tripVotes', voteId);
    let voteDoc: Awaited<ReturnType<typeof getDoc>>;
    try {
      voteDoc = await getDoc(voteRef);
    } catch (error) {
      const didSync = await syncTripMembershipIfNeeded(tripId, error);
      if (didSync) {
        voteDoc = await getDoc(voteRef);
      } else {
        throw error;
      }
    }
    
    if (!voteDoc.exists()) {
      return null;
    }
    
    return convertVote(voteDoc.id, voteDoc.data() as FirestoreTripVote);
  } catch (error) {
    logger.error('[Voting] Error getting user vote', error instanceof Error ? error : new Error(String(error)));
    return null;
  }
};

/**
 * Get vote aggregate for a specific item
 */
export const getVoteAggregate = async (
  tripId: string,
  itemId: string,
  itemType: ItemType
): Promise<VoteAggregate | null> => {
  try {
    const aggregateRef = doc(aggregateCollection(), getAggregateDocId(tripId, itemId, itemType));
    const aggregateDoc = await getDoc(aggregateRef);
    if (!aggregateDoc.exists()) {
      return emptyAggregate(itemId, itemType);
    }
    return toVoteAggregate(itemId, itemType, aggregateDoc.data() as FirestoreVoteAggregate);
  } catch (error) {
    logger.error('[Voting] Error getting vote aggregate', error instanceof Error ? error : new Error(String(error)));
    return null;
  }
};

/**
 * Get vote aggregates for multiple items
 */
export const getVoteAggregates = async (
  tripId: string,
  itemIds: string[],
  itemType: ItemType,
  options?: { maxItemIds?: number },
): Promise<Map<string, VoteAggregate>> => {
  try {
    const aggregates = new Map<string, VoteAggregate>();
    if (itemIds.length === 0) {
      return aggregates;
    }
    const maxItemIds = Math.max(1, Math.min(500, options?.maxItemIds ?? 200));
    const scopedItemIds = itemIds.slice(0, maxItemIds);
    const aggregateIds = scopedItemIds.map((itemId) => getAggregateDocId(tripId, itemId, itemType));
    const chunkSize = 30;
    for (let i = 0; i < aggregateIds.length; i += chunkSize) {
      const chunk = aggregateIds.slice(i, i + chunkSize);
      const q = query(aggregateCollection(), where(documentId(), 'in', chunk));
      const snapshot = await getDocs(q);
      snapshot.forEach((docSnap) => {
        const data = docSnap.data() as FirestoreVoteAggregate;
        const parsed = toVoteAggregate(data.itemId, data.itemType, data);
        aggregates.set(`${parsed.itemId}_${parsed.itemType}`, parsed);
      });
    }

    // Return default aggregates for items with no votes so consumers have stable entries.
    for (const itemId of scopedItemIds) {
      const key = `${itemId}_${itemType}`;
      if (!aggregates.has(key)) {
        aggregates.set(key, emptyAggregate(itemId, itemType));
      }
    }
    return aggregates;
  } catch (error) {
    logger.error('[Voting] Error getting vote aggregates', error instanceof Error ? error : new Error(String(error)));
    return new Map();
  }
};

/**
 * Get top voted items for a trip
 */
export const getTopVotedItems = async (
  tripId: string,
  itemType?: ItemType,
  limit: number = 10
): Promise<Array<{ itemId: string; itemType: ItemType; score: number; upvotes: number; downvotes: number }>> => {
  try {
    let q = query(aggregateCollection(), where('tripId', '==', tripId));
    if (itemType) {
      q = query(q, where('itemType', '==', itemType));
    }
    const snapshot = await getDocs(q);
    const items = snapshot.docs
      .map((docSnap) => {
        const data = docSnap.data() as FirestoreVoteAggregate;
        return toVoteAggregate(data.itemId, data.itemType, data);
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map((agg) => ({
        itemId: agg.itemId,
        itemType: agg.itemType,
        score: agg.score,
        upvotes: agg.upvotes,
        downvotes: agg.downvotes,
      }));
    
    return items;
  } catch (error) {
    logger.error('[Voting] Error getting top voted items', error instanceof Error ? error : new Error(String(error)));
    return [];
  }
};

/**
 * Subscribe to real-time vote updates for a trip
 */
export const subscribeToVotes = (
  tripId: string,
  callback: (aggregates: VoteAggregate[]) => void
): Unsubscribe => {
  const q = query(aggregateCollection(), where('tripId', '==', tripId));
  
  return onSnapshot(
    q,
    (snapshot) => {
      const aggregates = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as FirestoreVoteAggregate;
        return toVoteAggregate(data.itemId, data.itemType, data);
      });
      callback(aggregates);
    },
    (error) => {
      logger.error('[Voting] Error in vote subscription', error);
      void syncTripMembershipIfNeeded(tripId, error).catch(() => {});
      callback([]);
    }
  );
};

/**
 * Subscribe to vote updates for a specific item
 */
export const subscribeToItemVotes = (
  tripId: string,
  itemId: string,
  itemType: ItemType,
  callback: (aggregate: VoteAggregate | null) => void
): Unsubscribe => {
  const aggregateRef = doc(aggregateCollection(), getAggregateDocId(tripId, itemId, itemType));
  
  return onSnapshot(
    aggregateRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(emptyAggregate(itemId, itemType));
        return;
      }
      const data = snapshot.data() as FirestoreVoteAggregate;
      callback(toVoteAggregate(itemId, itemType, data));
    },
    (error) => {
      logger.error('[Voting] Error in item vote subscription', error);
      void syncTripMembershipIfNeeded(tripId, error).catch(() => {});
      callback(null);
    }
  );
};

